import { useEffect, useSyncExternalStore } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { useAppStatus } from "../../lib/app-status";
import { MaintenanceScreen } from "../settings/MaintenanceScreen";
import { Splash } from "../Splash";
import { RoomJoinError } from "./RoomJoinError";
import { Stage } from "../stage/Stage";
import { Button } from "../ui/button";
import { useRoom, useUnlockRoom } from "../../lib/rooms";
import { developmentSessionToken, useAuth } from "../../auth/auth";
import { RoomControllerProvider } from "../../room/RoomContext";
import { activeCall, useActiveCall, useMaintenanceResets } from "../../room/active-call";
import { JoinPasscodeForm } from "./JoinPasscodeForm";

const entryTitle = "my-6 font-heading text-4xl leading-tight font-normal tracking-tighter";
const entryText = "my-3.5 text-sm leading-loose text-muted-foreground";
const noSubscription = () => () => {};

// This boundary survives entry attempts. Only a successfully joined call may
// stay mounted during maintenance, including while reconnecting.
export function RoomPage() {
  const status = useAppStatus();
  const { roomId } = useParams({ from: "/room/$roomId" });
  const controller = useActiveCall(roomId)?.controller ?? null;
  const attempt = useMaintenanceResets();
  const hasJoinedRoom = useSyncExternalStore(
    controller?.store.subscribe ?? noSubscription,
    () => controller?.store.getState().connection.hasJoinedRoom ?? false,
  );
  if (status.maintenance && !hasJoinedRoom)
    return <MaintenanceScreen message={status.data?.maintenance.message} />;
  if (status.isPending) return <Splash />;
  return <RoomEntry key={attempt} roomId={roomId} />;
}

// A view of the app-level call: leaving this route never ends it.
function RoomEntry({ roomId }: { roomId: string }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: room, error: loadError } = useRoom(roomId);
  const unlock = useUnlockRoom(roomId);
  const error = (unlock.error || loadError)?.message || "";
  const busy = unlock.isPending;
  const call = useActiveCall(roomId);
  const controller = call?.controller ?? null;
  const connectionError = call?.error ?? "";
  const ready = room && !room.requiresPassword;
  const { user, authMode } = auth;
  const join = () => {
    if (!user) return;
    activeCall.ensureCall(roomId, user, authMode === "mock" ? developmentSessionToken() : "");
  };
  useEffect(() => {
    if (ready) join();
    // The signed-in user only changes by reloading the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- authMode and the user object are fixed for the page lifetime; adding them would restart the call mid-session
  }, [ready, roomId, user?.email]);
  // A failed join is not a call, so leaving the route clears it.
  useEffect(() => () => activeCall.discardFailed(roomId), [roomId]);
  function submitPassword(password: string) {
    if (busy) return;
    unlock.mutate(password);
  }
  return (
    <>
      {ready && controller ? (
        <RoomControllerProvider value={controller}>
          <div className="app-shell h-full w-full bg-black" hidden={Boolean(connectionError)}>
            <Stage
              room={room}
              user={auth.user}
              onSignOut={auth.signOut}
              onBack={() => void navigate({ to: "/" })}
            />
          </div>
        </RoomControllerProvider>
      ) : null}
      {!ready || !controller || connectionError ? (
        <main className="grid h-full place-items-center bg-background p-6 text-foreground">
          <section className="w-[min(24.375rem,100%)]">
            <Button asChild variant="ghost" className="mb-9 -ml-3 text-muted-foreground">
              <Link to="/">
                <ArrowLeft size={16} />
                {t("home.back")}
              </Link>
            </Button>
            {room?.requiresPassword && error !== "server-full" ? (
              <>
                <h1 className={entryTitle}>{t("home.protectedTitle")}</h1>
                <p className={entryText}>{t("home.protectedBody", { name: room.name })}</p>
                <JoinPasscodeForm busy={busy} error={error} onSubmit={submitPassword} />
              </>
            ) : error || connectionError ? (
              <RoomJoinError
                code={connectionError || error}
                onRetry={() => {
                  if (!connectionError) return window.location.reload();
                  // A failed join restarts through the owner; the document stays.
                  activeCall.discardFailed(roomId);
                  join();
                }}
              />
            ) : (
              <p role="status" className={entryText}>
                {t("home.joining")}
              </p>
            )}
          </section>
        </main>
      ) : null}
    </>
  );
}
